import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import useTableHeader, { TableHeaderProps } from 'shared/components/table/components/table-header/hooks';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

const TableHeader = <DataType extends object, Key extends NestedKeyof<DataType>>(
  props: Readonly<TableHeaderProps<DataType, Key>>,
): React.JSX.Element => {
  const { allRowsSelected } = useTableHeader(props);

  const { columns, orderDirection, orderBy, onRequestSort, hasOverflowMenu, rowsAreSelectable, onSelectAllRowsChanged } = props;

  const renderHeaderCell = (
    column: ColumnDefinition<DataType, Key>,
    index: number,
    orderBy: string,
    orderDirection: 'asc' | 'desc' | undefined,
    onRequestSort: (key: Key) => void,
  ): React.JSX.Element => {
    if (column.renderCustomHeader) {
      return (
        <TableCell className="table-cell" data-testid={`table-header-${index}`} key={`table-header-${column.key}`}>
          {column.renderCustomHeader()}
        </TableCell>
      );
    }
    if (column.disableSort) {
      return (
        <TableCell className="table-cell" data-testid={`table-header-${index}`} key={`table-header-${column.key}`}>
          {column.label}
        </TableCell>
      );
    }
    return (
      <TableCell className="table-cell" data-testid={`table-header-${index}`} key={`table-header-${column.key}`}>
        <TableSortLabel
          data-testid={`${column.key.toString()}-sorter`}
          active={orderBy === column.key}
          direction={orderBy === column.key ? orderDirection : 'asc'}
          onClick={() => onRequestSort(column.key)}
        >
          {column.label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <TableHead>
      <TableRow>
        {rowsAreSelectable && !onSelectAllRowsChanged && <TableCell padding="checkbox" />}
        {rowsAreSelectable && onSelectAllRowsChanged && (
          <TableCell
            padding="checkbox"
            sx={{
              '&.MuiTableCell-root.MuiTableCell-head:first-of-type': {
                padding: '0 8px',
              },
            }}
          >
            <Checkbox
              value={allRowsSelected ?? false}
              checked={allRowsSelected ?? false}
              onChange={(_, checked) => {
                if (onSelectAllRowsChanged) onSelectAllRowsChanged(checked);
              }}
            />
          </TableCell>
        )}
        {columns.map((column, index) => renderHeaderCell(column, index, orderBy, orderDirection, onRequestSort))}
        {hasOverflowMenu && <TableCell />}
      </TableRow>
    </TableHead>
  );
};

export default TableHeader;
